import { format, isSameDay, parseISO } from 'date-fns';
import { SlideOver } from '@/components/calendar/panels/SlideOver';
import { useDeleteNoteMutation } from '@/hooks/calendar';
import type { CalendarEvent } from '@/api/events';
import type { CalendarNote } from '@/api/notes';
import type { CalendarTask } from '@/api/tasks';

export type DayPanelSection = 'events' | 'tasks' | 'notes';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  section: DayPanelSection;
  selected: Date | null;
  mode?: 'day' | 'range';
  rangeLabel?: string;
  activeCalendarId?: string;
  dayEvents: CalendarEvent[];
  tasks: CalendarTask[];
  notes: CalendarNote[];
  tasksLoading: boolean;
  notesLoading: boolean;
};

export function DayItemsPanel({
  isOpen,
  onClose,
  section,
  selected,
  mode = 'day',
  rangeLabel,
  dayEvents,
  tasks,
  notes,
}: Props) {
  const deleteNote = useDeleteNoteMutation();

  if (!isOpen) return null;

  const handleDeleteNote = (note: CalendarNote) => {
    if (deleteNote.isPending) return;
    const label = note.title?.trim() || note.body?.trim() || 'this note';
    if (!window.confirm(`Delete ${label.length > 60 ? `${label.slice(0, 60)}…` : label}?`)) return;
    deleteNote.mutate(note.id);
  };

  const tasksForList = mode === 'day' && selected
    ? tasks.filter((t) => (t.dueAt ? isSameDay(parseISO(t.dueAt), selected) : false))
    : tasks.filter((t) => Boolean(t.dueAt));

  const notesForList = mode === 'day' && selected
    ? notes.filter((n) => (n.happensAt ? isSameDay(parseISO(n.happensAt), selected) : false))
    : notes.filter((n) => Boolean(n.happensAt));

  return (
    <SlideOver onClose={onClose} widthClass="max-w-lg" labelledBy="day-items-panel-title">
      {(close) => (
        <>
        <header className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-100 bg-white px-8 py-5 dark:border-white/5 dark:bg-card">
          <div>
            <h3 id="day-items-panel-title" className="text-xl font-bold tracking-tight text-[#0f172b] dark:text-white">
              {section === 'events' ? 'Events' : section === 'tasks' ? 'Tasks' : 'Notes'}
            </h3>
            <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">
              {mode === 'day'
                ? selected
                  ? format(selected, 'MMMM d, yyyy')
                  : 'Select a date'
                : `All in ${rangeLabel ?? 'selected range'}`}
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F3F4F8] text-slate-500 transition-colors hover:bg-[#E8EAF2] active:scale-95 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
          >
            <CloseIcon />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-8 py-8 space-y-8 custom-scrollbar">
          {section === 'events' ? (
            <div className="space-y-6">
              <ul className="space-y-4">
                {dayEvents.length === 0 ? (
                  <EmptyState message={mode === 'day' ? 'No events on this day.' : 'No events in this range.'} />
                ) : (
                  dayEvents.map((ev) => (
                    <li key={ev.id} className="rounded-2xl border border-slate-100 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-[#5B6CF6]/40 hover:shadow-md dark:border-white/5 dark:bg-card group transition-all">
                      <div className="flex items-center justify-between mb-2">
                        <span className="rounded-full bg-[#EEF2FF] px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#5B47E8] dark:bg-[#1C1F3F] dark:text-[#8B7CFF]">
                          Event
                        </span>
                        <span className="text-[11px] font-medium text-muted-foreground">
                          {ev.allDay ? 'All day' : `${format(parseISO(ev.startsAt), 'p')} - ${format(parseISO(ev.endsAt), 'p')}`}
                        </span>
                      </div>
                      <p className="font-bold text-foreground group-hover:text-[#5B47E8] transition-colors">{ev.title}</p>
                      {ev.description && <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{ev.description}</p>}
                    </li>
                  ))
                )}
              </ul>
            </div>
          ) : null}

          {section === 'tasks' ? (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">
                  {mode === 'day' ? `Due today (${tasksForList.length})` : `Due in range (${tasksForList.length})`}
                </p>
              </div>
              <ul className="space-y-3">
                {tasksForList.length === 0 ? (
                  <EmptyState message={mode === 'day' ? 'No tasks due this day.' : 'No tasks due in this range.'} />
                ) : (
                  tasksForList.map((t) => (
                    <li key={t.id} className="rounded-2xl border border-slate-100 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-[#5B6CF6]/40 hover:shadow-md dark:border-white/5 dark:bg-card flex items-center justify-between group">
                      <div className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded-lg border-2 border-primary/20 flex items-center justify-center group-hover:border-primary transition-colors cursor-pointer">
                          <div className="w-2 h-2 rounded-sm bg-primary opacity-0 group-hover:opacity-20 transition-opacity" />
                        </div>
                        <span className="text-sm font-bold text-foreground">{t.title}</span>
                      </div>
                      {t.dueAt && (
                        <span className="text-[10px] font-medium text-muted-foreground bg-muted/50 px-2 py-1 rounded-md">
                          {format(parseISO(t.dueAt), 'MMM d, p')}
                        </span>
                      )}
                    </li>
                  ))
                )}
              </ul>
            </div>
          ) : null}

          {section === 'notes' ? (
            <div className="space-y-6">
              <ul className="space-y-4">
                {notesForList.length === 0 ? (
                  <EmptyState message={mode === 'day' ? 'No notes for this day.' : 'No notes in this range.'} />
                ) : (
                  notesForList.map((n) => {
                    const isDeleting = deleteNote.isPending && deleteNote.variables === n.id;
                    return (
                    <li key={n.id} className="rounded-2xl border border-slate-100 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-[#5B6CF6]/40 hover:shadow-md dark:border-white/5 dark:bg-card relative group">
                      {n.pinned && (
                        <div className="absolute top-4 right-10 text-primary">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" /></svg>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => handleDeleteNote(n)}
                        disabled={isDeleting}
                        title="Delete note"
                        aria-label="Delete note"
                        className="absolute top-3 right-3 rounded-lg p-1.5 text-muted-foreground/60 opacity-0 transition-all hover:bg-destructive/10 hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100 disabled:opacity-60"
                      >
                        {isDeleting ? <SpinnerIcon /> : <TrashIcon />}
                      </button>
                      {n.title && <p className="font-bold text-foreground mb-1 pr-12">{n.title}</p>}
                      <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap pr-6">{n.body}</p>
                    </li>
                    );
                  })
                )}
              </ul>
              {deleteNote.isError ? (
                <p className="text-xs font-medium text-destructive">Could not delete note. Please try again.</p>
              ) : null}
            </div>
          ) : null}
        </div>
        </>
      )}
    </SlideOver>
  );
}

function CloseIcon() { return <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>; }

function TrashIcon() { return <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>; }

function SpinnerIcon() { return <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>; }

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-[#5B6CF6]/30 bg-white px-6 py-14 text-center dark:border-[#8B7CFF]/30 dark:bg-card">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#EEF2FF] text-[#5B47E8] dark:bg-[#1C1F3F] dark:text-[#8B7CFF]">
        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </div>
      <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">{message}</p>
    </div>
  );
}
